/**
 * The provider away notice: the clinic sets dates and a short note, and its
 * patients see it in their app during that window.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Button, Card, Field, inputClass } from './ui';
import { IconCheck } from './icons';

interface Away {
  from: string | null;
  until: string | null;
  note: string | null;
}

export function AwayNoticeCard({ className = '' }: { className?: string }) {
  const qc = useQueryClient();
  const away = useQuery({ queryKey: ['away'], queryFn: () => api.get<Away>('/settings/away') });
  const [form, setForm] = useState({ from: '', until: '', note: '' });
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (away.data) setForm({ from: away.data.from ?? '', until: away.data.until ?? '', note: away.data.note ?? '' });
  }, [away.data]);
  const save = useMutation({
    mutationFn: (body: Away) => api.put<Away>('/settings/away', body),
    onSuccess: (data) => {
      qc.setQueryData(['away'], data);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2500);
    },
  });
  const valid = (form.from === '') === (form.until === '') && (!form.from || form.until >= form.from);
  return (
    <Card title="Away notice" subtitle="When the provider is away, patients see this in their app between these dates." className={className}>
      <div className="grid gap-3 sm:grid-cols-[160px_160px_1fr]" data-testid="away-form">
        <Field label="Away from"><input id="away-from" type="date" className={inputClass} value={form.from} onChange={(e) => setForm({ ...form, from: e.target.value })} /></Field>
        <Field label="Away until, last day"><input id="away-until" type="date" className={inputClass} value={form.until} onChange={(e) => setForm({ ...form, until: e.target.value })} /></Field>
        <Field label="Note for patients, optional" hint="For example: urgent questions, call the clinic.">
          <input id="away-note" className={inputClass} maxLength={400} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
        </Field>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Button disabled={!valid} loading={save.isPending} onClick={() => save.mutate({ from: form.from || null, until: form.until || null, note: form.note || null })}>
          Save
        </Button>
        {(form.from || form.until) && (
          <Button variant="secondary" onClick={() => save.mutate({ from: null, until: null, note: null })}>
            Clear
          </Button>
        )}
        {saved && (
          <span className="flex items-center gap-1 text-sm text-emerald-700" role="status">
            <IconCheck size={14} /> Saved
          </span>
        )}
        {save.error && <span className="text-sm text-red-600">{save.error instanceof Error ? save.error.message : 'Could not save.'}</span>}
      </div>
    </Card>
  );
}
